import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    // Close sibling accordions when one opens
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Stop event from bubbling up to parent elements (prevents closing menu drawer)
    event.stopPropagation();
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const hasOpenByDefaultMobile = this.hasAttribute('open-by-default-on-mobile');
    const hasOpenByDefaultDesktop = this.hasAttribute('open-by-default-on-desktop');
    const isMobile = isMobileBreakpoint();

    // If both attributes exist, always keep open (used in header mega menu context)
    if (hasOpenByDefaultMobile && hasOpenByDefaultDesktop) {
      this.details.open = true;
      return;
    }

    const shouldBeOpen =
      (isMobile && hasOpenByDefaultMobile) ||
      (!isMobile && hasOpenByDefaultDesktop);

    this.details.open = shouldBeOpen;
  }

  /**
   * Ensures only one accordion is open at a time within the same accordion group
   * Looks for closest parent container with data-accordion-group attribute
   * If not found, scopes to the parent block element
   */
  #handleToggle = () => {
    if (!this.details.open) return;

    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Don't close siblings if accordion behavior is disabled
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      return;
    }

    // Find the closest accordion group container
    // Priority: data-accordion-group > specific section types > parent block
    const accordionGroup =
      this.closest('[data-accordion-group]') ||
      this.closest('faq-section') ||
      this.closest('.block') ||
      this.parentElement;

    if (!accordionGroup) return;

    /** @type {NodeListOf<HTMLElement>} */
    const siblingAccordions = accordionGroup.querySelectorAll('accordion-custom');

    siblingAccordions.forEach((accordion) => {
      // Skip if it's the current accordion
      if (accordion === this) return;

      // Skip if this accordion is a descendant of the current accordion (nested)
      if (this.contains(accordion)) return;

      // Skip if not a child of the same group
      if (!accordionGroup.contains(accordion)) return;

      const details = accordion.querySelector('details');
      if (details && details.open) {
        details.removeAttribute('open');
      }
    });
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
